import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, ExternalLink, Copy, MousePointer, Keyboard, Brain } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAITextRecognition } from '@/hooks/useAITextRecognition';

interface EnhancedPDFViewerProps {
  pdfUrl?: string;
  fileName?: string;
  onTextSelect?: (selectedText: string) => void;
  onAIAnalysis?: (analysis: any) => void;
  enableAIFeatures?: boolean;
}

const EnhancedPDFViewer: React.FC<EnhancedPDFViewerProps> = ({
  pdfUrl,
  fileName = "Document",
  onTextSelect,
  onAIAnalysis,
  enableAIFeatures = false
}) => {
  const [selectedText, setSelectedText] = useState<string>('');
  const [showSelectionFeedback, setShowSelectionFeedback] = useState(false);
  const [selectionMethod, setSelectionMethod] = useState<'manual' | 'doubleclick' | ''>('');
  const [aiAnalysis, setAiAnalysis] = useState<any>(null);
  const [showAIInsights, setShowAIInsights] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { toast } = useToast();
  const { analyzeText, getQualityScore } = useAITextRecognition();

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = fileName;
      link.click();
    }
  };

  const handleOpenInNewTab = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  const handleCopySelected = () => {
    if (selectedText) {
      navigator.clipboard.writeText(selectedText).then(() => {
        toast({
          title: "Text Copied",
          description: `Copied: "${selectedText.substring(0, 50)}${selectedText.length > 50 ? '...' : ''}"`,
        });
      });
    }
  };

  const showTextSelection = async (text: string, method: 'manual' | 'doubleclick') => {
    setSelectedText(text);
    setSelectionMethod(method);
    setShowSelectionFeedback(true);
    onTextSelect?.(text);
    
    // AI Analysis if enabled
    if (enableAIFeatures && text.trim().length > 2) {
      try {
        const analysis = await analyzeText(text);
        const quality = getQualityScore(text);
        
        setAiAnalysis({ analysis, quality });
        onAIAnalysis?.({ analysis, quality, text });
        
        if (analysis.confidence > 0.7) {
          setShowAIInsights(true);
          setTimeout(() => setShowAIInsights(false), 5000);
        }
      } catch (error) {
        console.warn('AI analysis error:', error);
      }
    }
    
    // Auto-hide feedback after 3 seconds
    setTimeout(() => {
      setShowSelectionFeedback(false);
    }, 3000);

    // Enhanced success toast with AI insights
    toast({
      title: "Text Selected",
      description: enableAIFeatures && aiAnalysis?.analysis 
        ? `${aiAnalysis.analysis.category.replace('_', ' ')} detected (${Math.round(aiAnalysis.analysis.confidence * 100)}%)`
        : `"${text.substring(0, 40)}${text.length > 40 ? '...' : ''}"`,
      duration: 2000
    });
  };

  // Enhanced PostMessage handler for PDF.js communication
  const handlePDFMessage = (event: MessageEvent) => {
    const allowedOrigins = [
      'https://mozilla.github.io',
      window.location.origin,
      'null' // For local file access
    ];
    
    if (!allowedOrigins.includes(event.origin) && event.origin !== 'null') {
      return;
    }
    
    try {
      const data = event.data;
      if (data.type === 'textSelected' && data.text && data.text.trim()) {
        showTextSelection(data.text.trim(), data.method === 'doubleclick' ? 'doubleclick' : 'manual');
      }
    } catch (error) {
      console.warn('Error handling PDF message:', error);
    }
  };

  // Enhanced PostMessage listener for PDF.js
  useEffect(() => {
    window.addEventListener('message', handlePDFMessage);
    return () => window.removeEventListener('message', handlePDFMessage);
  }, [onTextSelect]);

  // Enhanced iframe text selection with better error handling and PDF.js injection
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !pdfUrl) return;

    let iframeLoadTimeout: NodeJS.Timeout;

    const setupSelectionHandlers = () => {
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!iframeDoc) {
          console.warn('Cannot access iframe document - cross-origin restrictions may apply');
          return;
        }

        // Enhanced script injection with error handling
        const script = iframeDoc.createElement('script');
        script.textContent = `
          (function() {
            try {
              // Enhanced double-click handler with debouncing
              let doubleClickTimeout;
              document.addEventListener('dblclick', function(e) {
                clearTimeout(doubleClickTimeout);
                doubleClickTimeout = setTimeout(() => {
                  const selection = window.getSelection();
                  if (selection && selection.toString().trim()) {
                    const selectedText = selection.toString().trim();
                    if (selectedText.length > 2) {
                      parent.postMessage({
                        type: 'textSelected',
                        text: selectedText,
                        method: 'doubleclick'
                      }, '*');
                    }
                  }
                }, 100);
              });
              
              // Enhanced click handler for manual selections
              document.addEventListener('mouseup', function(e) {
                setTimeout(() => {
                  const selection = window.getSelection();
                  if (selection && selection.toString().trim()) {
                    const selectedText = selection.toString().trim();
                    if (selectedText.length > 2) {
                      parent.postMessage({
                        type: 'textSelected',
                        text: selectedText,
                        method: 'manual'
                      }, '*');
                    }
                  }
                }, 50);
              });

              // Enhanced keyboard shortcuts for text selection
              document.addEventListener('keydown', function(e) {
                if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
                  setTimeout(() => {
                    const selection = window.getSelection();
                    if (selection && selection.toString().trim()) {
                      const selectedText = selection.toString().trim();
                      parent.postMessage({
                        type: 'textSelected',
                        text: selectedText,
                        method: 'keyboard'
                      }, '*');
                    }
                  }, 50);
                }
              });
            } catch (error) {
              console.warn('PDF.js script injection error:', error);
            }
          })();
        `;
        
        if (iframeDoc.head) {
          iframeDoc.head.appendChild(script);
        } else {
          console.warn('Cannot inject script - iframe head not accessible');
        }

        // Fallback direct event handlers
        const handleDoubleClick = (e: Event) => {
          try {
            const selection = iframeDoc.getSelection();
            if (selection && selection.toString().trim()) {
              showTextSelection(selection.toString().trim(), 'doubleclick');
            }
          } catch (error) {
            console.log('Cross-origin text selection blocked');
          }
        };

        const handleMouseUp = () => {
          try {
            const selection = iframeDoc.getSelection();
            if (selection && selection.toString().trim()) {
              showTextSelection(selection.toString().trim(), 'manual');
            }
          } catch (error) {
            // Cross-origin restrictions
          }
        };

        iframeDoc.addEventListener('dblclick', handleDoubleClick);
        iframeDoc.addEventListener('mouseup', handleMouseUp);

        return () => {
          try {
            iframeDoc.removeEventListener('dblclick', handleDoubleClick);
            iframeDoc.removeEventListener('mouseup', handleMouseUp);
          } catch (error) {
            // Cleanup error - ignore
          }
        };
      } catch (error) {
        console.warn('Enhanced iframe setup error (this is normal for cross-origin PDFs):', error);
      }
    };

    const handleIframeLoad = () => {
      clearTimeout(iframeLoadTimeout);
      // Give PDF.js time to render before injecting scripts
      setTimeout(setupSelectionHandlers, 2000);
    };

    iframe.addEventListener('load', handleIframeLoad);

    // Fallback timeout in case load event doesn't fire
    iframeLoadTimeout = setTimeout(() => {
      setupSelectionHandlers();
    }, 4000);

    return () => {
      clearTimeout(iframeLoadTimeout);
      iframe.removeEventListener('load', handleIframeLoad);
    };
  }, [pdfUrl]);

  // Global selection listener as backup
  useEffect(() => {
    const handleGlobalSelectionChange = () => {
      try {
        const selection = window.getSelection();
        if (selection && selection.toString().trim()) {
          const text = selection.toString().trim();
          // Only capture if it's substantial text (more than 2 characters)
          if (text.length > 2) {
            showTextSelection(text, 'manual');
          }
        }
      } catch (error) {
        // Ignore selection errors
      }
    };

    // Throttle selection events
    let selectionTimeout: NodeJS.Timeout;
    const throttledHandler = () => {
      clearTimeout(selectionTimeout);
      selectionTimeout = setTimeout(handleGlobalSelectionChange, 300);
    };

    document.addEventListener('selectionchange', throttledHandler);
    return () => {
      clearTimeout(selectionTimeout);
      document.removeEventListener('selectionchange', throttledHandler);
    };
  }, []);

  if (!pdfUrl) {
    return (
      <div className="h-full flex items-center justify-center bg-muted/30">
        <div className="text-center text-muted-foreground space-y-2">
          <div className="text-lg">No PDF available</div>
          <div className="text-sm">Upload a PDF file to begin review</div>
        </div>
      </div>
    );
  }

  const pdfJSUrl = `https://mozilla.github.io/pdf.js/web/viewer.html?file=${encodeURIComponent(pdfUrl)}`;

  return (
    <div className="h-full flex flex-col bg-background relative">
      {/* Enhanced Header with Instructions */}
      <div className="flex flex-col border-b bg-card">
        <div className="flex items-center justify-between p-3">
          <div className="text-sm font-medium truncate">{fileName}</div>
          <div className="flex items-center gap-2">
            {selectedText && (
              <Button size="sm" variant="outline" onClick={handleCopySelected}>
                <Copy className="h-4 w-4 mr-1" />
                Copy Selected
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={handleDownload}>
              <Download className="h-4 w-4 mr-1" />
              Download
            </Button>
            <Button size="sm" variant="outline" onClick={handleOpenInNewTab}>
              <ExternalLink className="h-4 w-4 mr-1" />
              Open
            </Button>
          </div>
        </div>
        
        {/* Instructions */}
        <div className="px-3 pb-3 text-xs text-muted-foreground bg-muted/30 border-t">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <MousePointer className="h-3 w-3" />
              Select text to copy data
            </div>
            <div className="flex items-center gap-1">
              <Keyboard className="h-3 w-3" />
              Focus a field below, then select text to auto-paste
            </div>
            {enableAIFeatures && (
              <div className="flex items-center gap-1">
                <Brain className="h-3 w-3" />
                AI-powered field suggestions
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Text Selection Feedback */}
      {showSelectionFeedback && selectedText && (
        <div className="absolute top-20 left-4 right-4 z-20 bg-primary text-primary-foreground px-3 py-2 rounded-md shadow-lg border animate-in slide-in-from-top-2 duration-300">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">
                {selectionMethod === 'doubleclick' ? 'Double-click' : 'Manual'} Selection
              </Badge>
              <span className="text-sm">
                "{selectedText.substring(0, 50)}{selectedText.length > 50 ? '...' : ''}"
              </span>
              {enableAIFeatures && aiAnalysis?.analysis && (
                <Badge variant="outline" className="text-xs bg-background text-foreground">
                  {aiAnalysis.analysis.category.replace('_', ' ')} • {Math.round(aiAnalysis.analysis.confidence * 100)}%
                </Badge>
              )}
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 text-primary-foreground hover:text-primary-foreground/80"
              onClick={() => setShowSelectionFeedback(false)}
            >
              ×
            </Button>
          </div>
        </div>
      )}

      {/* AI Insights Overlay */}
      {enableAIFeatures && showAIInsights && aiAnalysis?.analysis && (
        <div className="absolute top-32 left-4 right-4 z-20 bg-background border rounded-md shadow-lg p-3 animate-in slide-in-from-top-2 duration-300">
          <div className="flex items-center gap-2 mb-2">
            <Brain className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">AI Analysis</span>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 ml-auto"
              onClick={() => setShowAIInsights(false)}
            >
              ×
            </Button>
          </div>
          <div className="space-y-1 text-xs">
            <div className="flex items-center gap-2">
              <span>Category:</span>
              <Badge variant="secondary">{aiAnalysis.analysis.category.replace('_', ' ')}</Badge>
              <span className="text-muted-foreground">
                {Math.round(aiAnalysis.analysis.confidence * 100)}% confidence
              </span>
            </div>
            {aiAnalysis.quality && (
              <div className="flex items-center gap-2">
                <span>Quality:</span>
                <span className={aiAnalysis.quality.score > 0.7 ? 'text-green-600' : 
                               aiAnalysis.quality.score > 0.4 ? 'text-yellow-600' : 'text-red-600'}>
                  {Math.round(aiAnalysis.quality.score * 100)}%
                </span>
              </div>
            )}
            <div className="text-muted-foreground">
              Language: {aiAnalysis.analysis.language.toUpperCase()}
            </div>
          </div>
        </div>
      )}

      {/* Enhanced PDF.js Iframe with proper isolation */}
      <div className="flex-1 relative overflow-hidden">
        <iframe
          ref={iframeRef}
          src={pdfJSUrl}
          className="w-full h-full border-0 bg-white"
          title={fileName}
          allow="clipboard-read; clipboard-write"
          style={{
            isolation: 'isolate',
            position: 'relative',
            zIndex: 1
          }}
        />
      </div>
    </div>
  );
};

export default EnhancedPDFViewer;